import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ENTITY_URL_PARAMS, type GlobalPanelEntityType } from './panel-stack-store';

export interface PinnedItem {
  id: string;
  entityType: 'company' | 'contact' | 'job' | 'sequence' | 'custom-list' | 'task' | 'deal';
  name: string;
  href: string;
}

interface PinnedState {
  pinnedItems: PinnedItem[];
  addPinnedItem: (item: PinnedItem) => void;
  removePinnedItem: (id: string, entityType: PinnedItem['entityType']) => void;
  isPinned: (id: string, entityType: PinnedItem['entityType']) => boolean;
  togglePin: (item: PinnedItem) => void;
}

// Map from PinnedItem entityType to GlobalPanelEntityType
const PINNED_TO_PANEL_TYPE: Record<PinnedItem['entityType'], GlobalPanelEntityType> = {
  company: 'company',
  contact: 'contact',
  job: 'job',
  sequence: 'sequence',
  'custom-list': 'customList',
  task: 'task',
  deal: 'deal',
};

/**
 * Migrate a pinned item href to path-less format (?panelStack=xxx&xxxId=xxx)
 * This allows panels to open on any page without navigation.
 */
function migratePinnedItemHref(item: PinnedItem): string {
  // If already path-less (starts with ?), keep as is
  if (item.href.startsWith('?')) {
    return item.href;
  }

  // Check for new panelStack format with path (e.g., /contacts?panelStack=contact&contactId=abc)
  if (item.href.includes('panelStack=')) {
    try {
      const url = new URL(item.href, 'http://localhost');
      // Extract just the query string
      return '?' + url.searchParams.toString();
    } catch {
      // Fall through to old format check
    }
  }

  // Check for old ?id= format (e.g., /contacts?id=abc)
  if (item.href.includes('?id=')) {
    try {
      const url = new URL(item.href, 'http://localhost');
      const oldId = url.searchParams.get('id');
      if (oldId) {
        const panelType = PINNED_TO_PANEL_TYPE[item.entityType];
        const idParam = ENTITY_URL_PARAMS[panelType];
        return `?panelStack=${panelType}&${idParam}=${oldId}`;
      }
    } catch {
      // If URL parsing fails, return original href
    }
  }

  // Return original href for non-query-param URLs (like /custom-lists/abc)
  return item.href;
}

export const usePinnedStore = create<PinnedState>()(
  persist(
    (set, get) => ({
      pinnedItems: [],

      addPinnedItem: (item) =>
        set((state) => {
          // Check if already pinned (by id + entityType)
          const exists = state.pinnedItems.some(
            (p) => p.id === item.id && p.entityType === item.entityType,
          );
          if (exists) return state;

          return {
            pinnedItems: [...state.pinnedItems, item],
          };
        }),

      removePinnedItem: (id, entityType) =>
        set((state) => ({
          pinnedItems: state.pinnedItems.filter(
            (p) => !(p.id === id && p.entityType === entityType),
          ),
        })),

      isPinned: (id, entityType) => {
        const { pinnedItems } = get();
        return pinnedItems.some((p) => p.id === id && p.entityType === entityType);
      },

      togglePin: (item) => {
        const { isPinned, addPinnedItem, removePinnedItem } = get();
        if (isPinned(item.id, item.entityType)) {
          removePinnedItem(item.id, item.entityType);
        } else {
          addPinnedItem(item);
        }
      },
    }),
    {
      name: 'pinned-items-storage',
      version: 2,
      migrate: (persistedState, _version) => {
        const state = persistedState as { pinnedItems?: PinnedItem[] };
        if (state?.pinnedItems) {
          // Migrate all pinned item hrefs to path-less format
          // Handles: version 0 (?id=), version 1 (/path?panelStack=), and already migrated (?panelStack=)
          state.pinnedItems = state.pinnedItems.map((item) => ({
            ...item,
            href: migratePinnedItemHref(item),
          }));
        }
        return state as PinnedState;
      },
    },
  ),
);
